import React, { useEffect, useState, useCallback } from "react";
import {
  Space,
  message,
  Table,
  Button,
  Typography,
  Popconfirm,
  Modal,
} from "antd";
import Util from "../../Util";
import { PAYMENT_STATUS, downloadAsCsv } from "./CommonUtils";

const { Text, Link } = Typography;

function PaymentLess100() {
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [lessHundredTable, setLessHundredTable] = useState([]);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailModalRow, setDetailModalRow] = useState(null);
  const [tablePagination, setTablePagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const loadLessHundredData = useCallback(async () => {
    setLoading(true);
    const data = await Util.sendGet("/api/moresheetStaff/getHytextsTopay", {});
    if (!data.success) {
      messageApi.error("มีข้อผิดพลาด");
      setLoading(false);
      return;
    }
    const payments = data.data.payments;
    const relaventSellers = data.data.relaventSellers;

    // Group by facebookID
    const facebookIDGroup = {}; // 100% revenue, need to calculate 60%
    for (const row of payments) {
      if (!facebookIDGroup.hasOwnProperty(row.author)) {
        facebookIDGroup[row.author] = {
          total: 0,
          paymentIds: [],
          paymentDetail: [],
        };
      }
      facebookIDGroup[row.author].total += row.total;
      facebookIDGroup[row.author].paymentIds.push(row._id);
      facebookIDGroup[row.author].paymentDetail.push({
        month: `${row.year}-${row.month}`,
        total: row.total.toFixed(2),
        payout: (row.total * 0.6).toFixed(2),
      });
    }

    // Group by Vendor ID / split those without
    const lessHundredTable = [];

    const vendorIdGroup = {};
    for (const seller of relaventSellers) {
      if (
        !facebookIDGroup.hasOwnProperty(seller.facebookID) ||
        !seller.htiVendorId
      )
        continue;
      const { total, paymentIds, paymentDetail } =
        facebookIDGroup[seller.facebookID];
      const splitBank = seller.bankInfo.split(",");
      const payBank = splitBank.length === 2 ? splitBank[0] : "Invalid";
      if (!vendorIdGroup.hasOwnProperty(seller.htiVendorId)) {
        vendorIdGroup[seller.htiVendorId] = {
          total: 0,
          paymentIds: [],
          paymentDetail: [],
          htiVendorName: seller.htiVendorName,
          peopleID: seller.peopleID,
          realName: seller.realName,
          payBank,
        };
      }
      vendorIdGroup[seller.htiVendorId].total += total;
      vendorIdGroup[seller.htiVendorId].paymentIds.push(...paymentIds);
      vendorIdGroup[seller.htiVendorId].paymentDetail.push(...paymentDetail);
    }

    Object.entries(vendorIdGroup).forEach(([key, val]) => {
      const payout = val.total * 0.6;
      const vendorData = {
        ...val,
        htiVendorId: key,
        payout: payout.toFixed(2),
        total: val.total.toFixed(2),
        scbNonScb: val.payBank === "ธ. ไทยพาณิชย์" ? "SCB" : "Non-SCB",
      };
      if (payout < 100) {
        lessHundredTable.push(vendorData);
      }
    });

    setLoading(false);
    return lessHundredTable;
  }, [messageApi]);

  useEffect(() => {
    async function fetchData() {
      const lessHundredTable = await loadLessHundredData();
      setLessHundredTable(lessHundredTable);
    }
    fetchData();
  }, [loadLessHundredData]);

  const updateStatus = async () => {
    setLoading(true);
    const result = await Util.sendPost(
      "/api/moresheetStaff/updatePaymentStatusByIds",
      {
        ids: lessHundredTable.flatMap((row) => row.paymentIds),
        paymentStatus: PAYMENT_STATUS.CARRIED_OVER,
        paymentPausedReason: "",
      }
    );
    setLoading(false);
    if (result.success) {
      messageApi.success(`อัพเดตสถานะสำเร็จ ${result.modifiedCount} รายการ`);
      // Reload data to reflect changes
      const lessHundredTable = await loadLessHundredData();
      setLessHundredTable(lessHundredTable);
    } else {
      messageApi.error(
        `อัพเดตสถานะไม่สำเร็จ: ${result.message || "เกิดข้อผิดพลาด"}`
      );
    }
  };

  const handleTableChange = (pagination) => {
    setTablePagination(pagination);
  };

  const showDetailModal = (row) => {
    setDetailModalRow(row);
    setDetailModalVisible(true);
  };

  const closeDetailModal = () => {
    setDetailModalVisible(false);
  };

  const VendorTables = () => {
    const validVendorColumns = [
      { title: "Vendor Id", dataIndex: "htiVendorId", key: "htiVendorId" },
      {
        title: "Vendor Name",
        dataIndex: "htiVendorName",
        key: "htiVendorName",
      },
      { title: "เลขบัตรประชาชน", dataIndex: "peopleID", key: "peopleID" },
      { title: "ชื่อจริง", dataIndex: "realName", key: "realName" },
      {
        title: "ยอดขาย",
        dataIndex: "total",
        key: "total",
        render: (text, row) => (
          <Link onClick={() => showDetailModal(row)}>{text}</Link>
        ),
      },
      {
        title: "ยอดจ่าย(60%)",
        dataIndex: "payout",
        key: "payout",
        render: (text, row) => (
          <Link onClick={() => showDetailModal(row)}>{text}</Link>
        ),
      },
      { title: "ธนาคาร", dataIndex: "payBank", key: "payBank" },
      { title: "SCB or Non-SCB", dataIndex: "scbNonScb", key: "scbNonScb" },
    ];

    return (
      <React.Fragment>
        <Button
          type="primary"
          onClick={() =>
            downloadAsCsv(
              lessHundredTable,
              validVendorColumns,
              "pending_payable_lt100.csv"
            )
          }
        >
          Download as CSV
        </Button>
        <br />

        <Popconfirm
          title="Confirm"
          description="เปลี่ยนสถานะรายการทั้งหมดเป็น 'ทบจนกว่ายอดจ่ายจะครบ 100 บาท'"
          onConfirm={updateStatus}
          onCancel={() => {}}
          okText="เปลี่ยนสถานะ"
          cancelText="ยกเลิก"
        >
          <Button type="primary" danger>
            ปรับสถานะเป็น "ทบจนกว่ายอดจ่ายจะครบ 100 บาท"
          </Button>
        </Popconfirm>
        <Table
          dataSource={lessHundredTable}
          columns={validVendorColumns}
          pagination={{
            ...tablePagination,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100", "200"],
            total: lessHundredTable.length,
          }}
          onChange={handleTableChange}
          rowKey="htiVendorId"
        />
      </React.Fragment>
    );
  };

  return (
    <Space direction="vertical" size={12}>
      {contextHolder}
      <Modal
        title="รายละเอียดยอดขายสะสม"
        open={detailModalVisible}
        onOk={closeDetailModal}
        onCancel={closeDetailModal}
        footer={[
          <Button key="ok" type="primary" onClick={closeDetailModal}>
            OK
          </Button>,
        ]}
      >
        <p>
          {detailModalRow?.htiVendorId}: {detailModalRow?.htiVendorName}
        </p>
        <Table
          dataSource={detailModalRow?.paymentDetail}
          columns={[
            { title: "เดือน", dataIndex: "month", key: "month" },
            { title: "ยอดขาย", dataIndex: "total", key: "total" },
            {
              title: "ยอดจ่าย (60%)",
              dataIndex: "payout",
              key: "payout",
            },
          ]}
          pagination={false}
          size="small"
        />
        <p>รวมยอดจ่าย {detailModalRow?.payout} บาท</p>
      </Modal>
      {loading ? <Text>Loading...</Text> : <VendorTables />}
    </Space>
  );
}

export default PaymentLess100;
